import React, {useCallback, useEffect, useState} from 'react';
import { Header } from './components/header/Header';
import { NewTimelineView } from './components/newTimelineView/TimelineView';
import { MyDialog } from './components/dialog/MyDialog';
import { getLoggedInUserAccount, getUserAccount, getUserData } from './firebase/Firestore';
import { useDispatch, useSelector } from 'react-redux';
import './App.css';
import { setAppDimensions, setAppView, setDialogOpen, setEmbed, setHideHeader, setLoggedInUser, setLoggedInUserAccount, setViewingUserAccount, setViewingUserCategories, setViewingUserEvents, setViewingUserFriends, setViewingUserTags, setViewingUserTrips, setFilters } from './store/Actions';
import { isViewDialog, parsePath } from './utils/Routing';
import { MapView } from './components/mapView/MapView';
import { getSideNavWidth } from './components/mapView/mapLayout';
import { FriendsView } from './components/friendsView/FriendsView';
import { CategoriesView } from './components/categoriesView/CategoriesView';
import { TripsView } from './components/tripsView/TripsView';
import { TagsView } from './components/tagsView/TagsView';
import { AboutView } from './components/aboutView/AboutView';
import { FriendDetailView } from './components/detailView/FriendDetailView';
import { CategoryDetailView } from './components/detailView/CategoryDetailView';
import { TripDetailView } from './components/detailView/TripDetailView';
import { TagDetailView } from './components/detailView/TagDetailView';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, GoogleAuthProvider, setPersistence, browserLocalPersistence, signInWithPopup } from "firebase/auth";
import SecondMemoriesIcon from './assets/SecondMemoriesIcon.svg';

const appStyle = {
  display:'flex', 
  flexDirection:'column'
}

function App() {
  const embed = useSelector(state=>state.embed)
  const hideHeader = useSelector(state=>state.hideHeader)
  const appWidth= useSelector(state=>state.appWidth);
  const appHeight = useSelector(state=>state.appHeight);
  const reduxAppView = useSelector(state=>state.appView);
  const sideNavOpen = useSelector(state=>state.sideNavOpen);
  const loggedInUserAccount = useSelector(state=>state.loggedInUserAccount);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [authInitialized, setAuthInitialized] = useState(false);
  const [viewingUserNotFound, setViewingUserNotFound] = useState('');
  const dispatch = useDispatch();

  const selectedCategoryIds = useSelector(state=>state.selectedCategoryIds);
  const selectedFriendIds = useSelector(state=>state.selectedFriendIds);
  const selectedTagIds = useSelector(state=>state.selectedTagIds);
  const selectedTripIds = useSelector(state=>state.selectedTripIds);
  const selectedStartDate = useSelector(state=>state.selectedStartDate);
  const selectedEndDate = useSelector(state=>state.selectedEndDate);

  const dialogOpenName = useSelector(state=>state.dialogOpen);

  const usernameFromPath = decodeURIComponent(location.pathname.split('/').filter(Boolean)[0] ?? '');
  const loggedInUsername = loggedInUserAccount?.username;
  const isInitialized = useSelector(state=>state.viewingUserEvents !== null);

  // The URL is the source of truth for which page is showing. Render directly
  // from the parsed pathname so that on a fresh load of, say, /joegordon/map
  // the Map view paints on the first frame instead of flashing the default
  // 'About' first while the URL→Redux effect catches up.
  const parsedRoute = parsePath(location.pathname);
  const view = parsedRoute.view;
  const detailEntityId = parsedRoute.viewDialogId;
  const detailTab = parsedRoute.viewDialogTab;

  const headerHeight = appWidth <= 560 ? 50 : 52;

  window.onresize = () => {
    dispatch(setAppDimensions(window.innerWidth, window.innerHeight));
  };

  const getAndSetViewingUser = useCallback(async (username) => {
    const account = await getUserAccount(username);
    if(account){
      setViewingUserNotFound('');
      dispatch(setViewingUserAccount(account));
      const userData = await getUserData(account.id);
      dispatch(setViewingUserCategories(userData.categories));
      dispatch(setViewingUserFriends(userData.friends));
      dispatch(setViewingUserTrips(userData.trips));
      dispatch(setViewingUserTags(userData.tags));
      dispatch(setViewingUserEvents(userData.events));
    }
    else
      setViewingUserNotFound(username);
  }, [dispatch]);

  const login = () => {
    const auth = getAuth();
    setPersistence(auth, browserLocalPersistence).then(()=>{
      signInWithPopup(auth, new GoogleAuthProvider())
      .catch((error) => {
        console.log('error logging in:', error)
      });
    });
  }

  const getAndSetFiltersFromSearchParams = useCallback(() => {
    const categoryIds = searchParams.get("categories")?.split(",").filter(Boolean) ?? [];
    const friendIds = searchParams.get("friends")?.split(",").filter(Boolean) ?? [];
    const tagIds = searchParams.get("tags")?.split(",").filter(Boolean) ?? [];
    const tripIds = searchParams.get("trips")?.split(",").filter(Boolean) ?? [];
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");
    const startDate = startDateParam ? new Date(startDateParam) : null;
    const endDate = endDateParam ? new Date(endDateParam) : null;
    dispatch(setFilters(categoryIds, friendIds, tagIds, tripIds, startDate, endDate));
  }, [dispatch, searchParams]);

  useEffect(()=>{
    dispatch(setEmbed(searchParams.get("embed")))
    dispatch(setHideHeader(searchParams.get("hideHeader") === "true"))
    getAndSetFiltersFromSearchParams();
  }, [dispatch, getAndSetFiltersFromSearchParams, searchParams])

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if(user){
        dispatch(setLoggedInUser(user));
        const account = await getLoggedInUserAccount(user);
        dispatch(setLoggedInUserAccount(account));
      }
      else
        dispatch(setLoggedInUser(null));

      setAuthInitialized(true);
    });

    return () => unsubscribe();
  }, [dispatch]);

  useEffect(() => {
    if(!authInitialized) return;

    if(usernameFromPath){
      getAndSetViewingUser(usernameFromPath);
      return;
    }

    if(loggedInUsername){
      navigate({
        pathname: `/${encodeURIComponent(loggedInUsername)}`,
        search: location.search,
      }, { replace: true });
    }
  }, [authInitialized, getAndSetViewingUser, location.search, loggedInUsername, navigate, usernameFromPath]);

  // /[username] (no section) → /[username]/about. Done as a separate
  // redirect so the auth/landing redirect above can stay focused on its job
  // of resolving / to the logged-in user. Skipped in embed mode, where we
  // never modify the URL.
  useEffect(() => {
    if(embed) return;
    if(!usernameFromPath) return;
    const parts = location.pathname.split('/').filter(Boolean);
    if(parts.length === 1){
      navigate({
        pathname: `/${parts[0]}/about`,
        search: location.search,
      }, { replace: true });
    }
  }, [embed, location.pathname, location.search, navigate, usernameFromPath]);

  useEffect(()=>{
    if(!isInitialized || embed) return;
    setSearchParams(params => {
      if(selectedCategoryIds.length > 0)
        params.set("categories", selectedCategoryIds.join(","));
      else
        params.delete("categories");

      if(selectedFriendIds.length > 0)
        params.set("friends", selectedFriendIds.join(","));
      else
        params.delete("friends");

      if(selectedTagIds.length > 0)
        params.set("tags", selectedTagIds.join(","));
      else
        params.delete("tags");

      if(selectedTripIds.length > 0)
        params.set("trips", selectedTripIds.join(","));
      else
        params.delete("trips");

      if(selectedStartDate && selectedEndDate){
        params.set("startDate", selectedStartDate instanceof Date ? selectedStartDate.toISOString() : selectedStartDate);
        params.set("endDate", selectedEndDate instanceof Date ? selectedEndDate.toISOString() : selectedEndDate);
      } else {
        params.delete("startDate");
        params.delete("endDate");
      }

      return params;
    }, { replace: true });
  }, [embed, selectedCategoryIds, selectedFriendIds, selectedTagIds, selectedTripIds, selectedStartDate, selectedEndDate, isInitialized, setSearchParams]);

  // ----- URL → Redux view sync -----------------------------------------
  // The URL is the single source of truth for which page is showing.
  // Render uses the URL-derived `view` directly (see above); this effect
  // just mirrors that into Redux so consumers reading `state.appView` (the
  // side nav highlight, etc.) stay in sync with the URL.
  //
  // Detail pages (/friends/[id] etc.) are now rendered as full views
  // rather than dialogs — see appContent below — so we no longer dispatch
  // setDialogOpen('View X', ...) from here. We still defensively close any
  // stale "View X" dialog dispatched by old code paths so it can't linger
  // in front of the new detail page.
  useEffect(() => {
    if(!isInitialized || embed) return;
    if(!usernameFromPath) return;

    if(parsedRoute.view !== reduxAppView){
      dispatch(setAppView(parsedRoute.view));
    }

    if(isViewDialog(dialogOpenName)){
      dispatch(setDialogOpen(''));
    }
  }, [
    parsedRoute.view,
    isInitialized,
    embed,
    usernameFromPath,
    reduxAppView,
    dialogOpenName,
    dispatch,
  ]);

  const sideNavOffset = sideNavOpen && !embed && appWidth > 560 && view !== 'Map'
    ? getSideNavWidth(appWidth)
    : 0;

  const appContentContainerStyle = {
    height: (appHeight - (hideHeader ? 0 : headerHeight)) + 'px',
    width: (appWidth - sideNavOffset) + 'px',
    marginLeft: sideNavOffset + 'px',
    transition: 'margin-left 160ms ease, width 160ms ease',
  }

  let appContent = <AboutView/>;
  switch(view) {
    case 'Map':
      appContent = <MapView/>;
      break;
    case 'Timeline':
      appContent = <NewTimelineView/>;
      break;
    case 'Friends':
      appContent = detailEntityId
        ? <FriendDetailView entityId={detailEntityId} tab={detailTab}/>
        : <FriendsView/>;
      break;
    case 'Categories':
      appContent = detailEntityId
        ? <CategoryDetailView entityId={detailEntityId} tab={detailTab}/>
        : <CategoriesView/>;
      break;
    case 'Trips':
      appContent = detailEntityId
        ? <TripDetailView entityId={detailEntityId} tab={detailTab}/>
        : <TripsView/>;
      break;
    case 'Tags':
      appContent = detailEntityId
        ? <TagDetailView entityId={detailEntityId} tab={detailTab}/>
        : <TagsView/>;
      break;
    case 'About':
    default:
      appContent = <AboutView/>;
      break;
  }

  if(!usernameFromPath && authInitialized && !loggedInUserAccount){
    return <LandingPage onSignIn={login}/>;
  }

  if(viewingUserNotFound){
    return <LandingPage onSignIn={login} message={`No Second Memories account exists for @${viewingUserNotFound}.`}/>;
  }

  if(!isInitialized){
    return (
      <div className="app-loading">
        <img className="app-loading__icon" src={SecondMemoriesIcon} alt=""/>
      </div>
    );
  }

  return (
    <>
      <div style={appStyle}>
        {!hideHeader && <Header/>}
        <div style={appContentContainerStyle}>
          {appContent}
        </div>
        <MyDialog/>
      </div>
    </>
  );
}

function LandingPage({onSignIn, message}) {
  return (
    <main className="landing-page">
      <section className="landing-page__content">
        <img className="landing-page__icon" src={SecondMemoriesIcon} alt=""/>
        <h1>Second Memories</h1>
        <p>Map the people, places, trips, and memories that shape a life.</p>
        {message && <div className="landing-page__message">{message}</div>}
        <button className="landing-page__button" type="button" onClick={onSignIn}>
          Sign in
        </button>
      </section>
    </main>
  );
}

export default App;
